import React, { FunctionComponent, useEffect, useState } from 'react';
import { loadScript } from '~/scriptLoader';

type WidgetOptions = Record<string, unknown>;

interface ModuleWidgetInstance {
  Component: FunctionComponent;
}

interface Module {
  Widget: any;
}

interface WidgetLoaderProps {
  loadingMessage?: string;
  module: string;
  onError?: (e: Error) => void;
  options: WidgetOptions;
  src: string;
}

declare global {
  interface Window {
    [key: string]: Module | undefined;
  }
}

const WidgetLoader = (props: WidgetLoaderProps): JSX.Element | null => {
  const [error, setError] = useState(false);
  const [Widget, setWidget] = useState<ModuleWidgetInstance | null>(null);
  const { module, onError, src } = props;

  useEffect(() => {
    loadScript(module, src)
      .then(() => {
        const windowModule = window[module];
        if (windowModule) {
          setWidget(new windowModule.Widget());
        } else {
          throw new Error(`Module ${module} does not exist in window`);
        }
      })
      .catch((e) => {
        setError(true);
        if (onError) {
          onError(e);
        }
      });
  }, [module, onError, src]);

  if (error) {
    return null;
  }

  try {
    return (
      <>
        {Widget === null && props.loadingMessage && (
          <span>{props.loadingMessage}</span>
        )}
        {Widget && <Widget.Component {...props.options} />}
      </>
    );
  } catch (e) {
    if (props.onError) {
      setError(true);
      props.onError(new Error(e));
    }
    return null;
  }
};

export default WidgetLoader;
